import { CostPreview, OrgCosts, ProjectCosts, type CostChange } from "@cloudserver/contracts";
import { useQuery } from "@tanstack/react-query";
import { API_BASE, api } from "./client.js";

/*
 * Costs (docs/costs.md). A project's costs live under its `projects` key, so
 * resource events refresh the forecast; the organisation view under `costs`.
 * The meter's `costs.updated` event refreshes both (`events.ts`).
 */

export const costKeys = {
  org: (month: string | undefined) => ["costs", month ?? "current"] as const,
  project: (projectId: string, month: string | undefined) =>
    ["projects", projectId, "costs", month ?? "current"] as const,
  preview: (projectId: string, change: CostChange | null) =>
    ["projects", projectId, "costs", "preview", change] as const,
};

const monthQuery = (month: string | undefined) => (month ? `?month=${month}` : "");

export const useProjectCosts = (projectId: string, month?: string) =>
  useQuery({
    queryKey: costKeys.project(projectId, month),
    queryFn: () =>
      api(`/projects/${projectId}/costs${monthQuery(month)}`, { schema: ProjectCosts }),
  });

export const useOrgCosts = (month?: string, enabled = true) =>
  useQuery({
    queryKey: costKeys.org(month),
    queryFn: () => api(`/costs${monthQuery(month)}`, { schema: OrgCosts }),
    enabled,
  });

/** Where the browser downloads the month as CSV (the session cookie goes with it). */
export const costsExportUrl = (projectId: string | null, month: string | undefined): string =>
  `${API_BASE}${projectId ? `/projects/${projectId}` : ""}/costs/export${monthQuery(month)}`;

/**
 * What a change would cost, asked while the form is filled in; null while
 * the form is not complete. Read-only for the API, so guests may ask too.
 */
export const useCostPreview = (projectId: string, change: CostChange | null) =>
  useQuery({
    queryKey: costKeys.preview(projectId, change),
    queryFn: () =>
      api(`/projects/${projectId}/costs/preview`, {
        method: "POST",
        body: change,
        schema: CostPreview,
      }),
    enabled: change !== null,
    staleTime: 30_000,
  });

/** The last 24 months, newest first, as `YYYY-MM` (UTC), for the month picker. */
export function recentMonths(now = new Date(), count = 24): string[] {
  return Array.from({ length: count }, (_, i) =>
    new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1)).toISOString().slice(0, 7),
  );
}
